import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Shield, Search, Heart, Home, Menu, X, PlusCircle, Sparkles } from 'lucide-react';
import { auth } from '../services/api';
import useShortlist from '../hooks/useShortlist';

function Navigation() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'provider' or 'guest'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { count: shortlistCount } = useShortlist();

  const [isCoupleLoggedIn, setIsCoupleLoggedIn] = useState(false);

  useEffect(() => {
    const provider = auth.getCurrentUser();
    setCurrentUser(provider);
    setUserType(provider ? 'provider' : 'guest');
    setIsCoupleLoggedIn(!!localStorage.getItem('coupleToken'));
  }, []);

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };

  return (
    <div className="bg-white shadow-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo/Brand */}
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              BridalConnect
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-3">
            {userType === 'provider' && currentUser && (
              <>
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Home className="w-4 h-4" />
                  Home
                </button>
                <button
                  onClick={() => navigate('/providers')}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Browse Providers
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Dashboard
                </button>
                {currentUser.is_super_admin && localStorage.getItem('impersonating') !== 'true' && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            )}

            {userType === 'guest' && (
              <>
                <button
                  onClick={() => navigate('/providers')}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Browse Providers
                </button>
                <button
                  onClick={() => navigate('/shortlist')}
                  className="px-4 py-2 text-pink-600 hover:bg-pink-50 rounded-lg transition-colors flex items-center gap-2 relative"
                >
                  <Heart className="w-4 h-4" />
                  Shortlist
                  {shortlistCount > 0 && (
                    <span className="bg-pink-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                      {shortlistCount}
                    </span>
                  )}
                </button>
                {isCoupleLoggedIn ? (
                  <button
                    onClick={() => navigate('/couple/dashboard')}
                    className="px-4 py-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-lg hover:from-rose-600 hover:to-pink-600 transition-all flex items-center gap-2 font-medium shadow-sm"
                  >
                    <Sparkles className="w-4 h-4" />
                    My Wedding Page
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/couple/register')}
                    className="px-4 py-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-lg hover:from-rose-600 hover:to-pink-600 transition-all flex items-center gap-2 font-medium shadow-sm"
                  >
                    <Sparkles className="w-4 h-4" />
                    Free Wedding Page
                  </button>
                )}
                <button
                  onClick={() => navigate('/apply')}
                  className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <PlusCircle className="w-4 h-4" />
                  Apply for Listing
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors text-sm"
                >
                  Provider Login
                </button>
              </>
            )}
          </div>

          {/* Mobile Hamburger Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 space-y-2">
            {userType === 'provider' && currentUser && (
              <>
                <button
                  onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                  className="w-full px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Home className="w-4 h-4" />
                  Home
                </button>
                <button
                  onClick={() => { navigate('/providers'); setMobileMenuOpen(false); }}
                  className="w-full px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Browse Providers
                </button>
                <button
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                  className="w-full px-4 py-3 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Dashboard
                </button>
                {currentUser.is_super_admin && localStorage.getItem('impersonating') !== 'true' && (
                  <button
                    onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                    className="w-full px-4 py-3 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </button>
                )}
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            )}

            {userType === 'guest' && (
              <>
                <button
                  onClick={() => { navigate('/providers'); setMobileMenuOpen(false); }}
                  className="w-full px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Browse Providers
                </button>
                <button
                  onClick={() => { navigate('/shortlist'); setMobileMenuOpen(false); }}
                  className="w-full px-4 py-3 text-pink-600 hover:bg-pink-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Heart className="w-4 h-4" />
                  Shortlist
                  {shortlistCount > 0 && (
                    <span className="bg-pink-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                      {shortlistCount}
                    </span>
                  )}
                </button>
                {isCoupleLoggedIn ? (
                  <button
                    onClick={() => { navigate('/couple/dashboard'); setMobileMenuOpen(false); }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-lg hover:from-rose-600 hover:to-pink-600 transition-all flex items-center gap-2 font-medium"
                  >
                    <Sparkles className="w-4 h-4" />
                    My Wedding Page
                  </button>
                ) : (
                  <button
                    onClick={() => { navigate('/couple/register'); setMobileMenuOpen(false); }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-lg hover:from-rose-600 hover:to-pink-600 transition-all flex items-center gap-2 font-medium"
                  >
                    <Sparkles className="w-4 h-4" />
                    Free Wedding Page
                  </button>
                )}
                <button
                  onClick={() => { navigate('/apply'); setMobileMenuOpen(false); }}
                  className="w-full px-4 py-3 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <PlusCircle className="w-4 h-4" />
                  Apply for Listing
                </button>
                <button
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  className="w-full px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2 text-sm"
                >
                  Provider Login
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Navigation;
